import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { X, Plus, Trash2, Upload, MessageCircle, Camera } from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';
import { sendWhatsAppMessage } from '../../utils/whatsapp';
import Select from 'react-select';

const CreateJobModal = ({ isOpen, onClose, onSuccess }) => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    jobDate: new Date().toISOString().split('T')[0],
    customerId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    customerState: '',
    customerGst: '',
    deviceCategory: '',
    deviceBrand: '',
    deviceModel: '',
    deviceImei: '',
    devicePassword: '',
    technicianName: '',
    technicianContact: '',
    estimatedCost: '',
    boxNumber: '',
    labourCost: '',
    advanceAmount: '',
    problemDescription: '',
    priority: 'NORMAL',
    expectedDate: ''
  });

  const [problems, setProblems] = useState([]);
  const [newProblem, setNewProblem] = useState('');
  const [accessories, setAccessories] = useState([]);
  const [newAccessory, setNewAccessory] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const [currentTechnicianId, setCurrentTechnicianId] = useState('');

  const [spares, setSpares] = useState([]);
  const [showAddSparesModal, setShowAddSparesModal] = useState(false);
  const [popupSelectedSpare, setPopupSelectedSpare] = useState(null);
  const [popupSparePrice, setPopupSparePrice] = useState('');
  const [popupSpareQty, setPopupSpareQty] = useState(1);
  const [tempSpares, setTempSpares] = useState([]);

  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [newProductData, setNewProductData] = useState({
    categoryId: '',
    brand: '',
    model: '',
    name: '',
    purchaseRate: '',
    saleRate: '',
    openingStock: '10'
  });

  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  const { data: productsData } = useQuery('products', async () => {
    const response = await api.get('/api/products');
    return response.data.data.products;
  });

  const { data: customersData } = useQuery('customers', async () => {
    const response = await api.get('/api/customers');
    return response.data.data.customers;
  });

  const { data: techniciansData } = useQuery(
    ['technicians', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/technicians', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    { enabled: !!selectedBranch }
  );

  const { data: categoriesData } = useQuery('categories', async () => {
    const response = await api.get('/api/categories');
    return response.data.data;
  }, {
    onSuccess: (data) => {
      if (data && data.length > 0 && !formData.deviceCategory) {
        setFormData(prev => ({ ...prev, deviceCategory: data[0].name }));
      }
    }
  });

  const createJobMutation = useMutation(
    async (jobData) => {
      const formDataToSend = new FormData();
      Object.keys(jobData).forEach(key => {
        if (key === 'problems' || key === 'accessories' || key === 'spares' || key === 'technicians') {
          formDataToSend.append(key, JSON.stringify(jobData[key]));
        } else if (key === 'attachments') {
          jobData[key].forEach(file => {
            formDataToSend.append('attachments', file);
          });
        } else if (jobData[key] !== undefined && jobData[key] !== null) {
          formDataToSend.append(key, jobData[key]);
        }
      });
      const response = await api.post('/api/jobs', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job created successfully!');
        queryClient.invalidateQueries('jobs');
        onSuccess?.();
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create job');
      }
    }
  );

  const createProductMutation = useMutation(
    async (productData) => {
      const response = await api.post('/api/products', productData);
      return response.data;
    },
    {
      onSuccess: (res) => {
        toast.success('Product created successfully!');
        queryClient.invalidateQueries('products');
        if (res.data) {
          const newProd = res.data;
          setPopupSelectedSpare({
            value: newProd.id,
            label: `${newProd.name} (${newProd.brand} ${newProd.model}) - Stock: ${newProd.currentStock}`,
            price: newProd.saleRate,
            stock: newProd.currentStock,
            name: newProd.name
          });
          setPopupSparePrice(newProd.saleRate.toString());
        }
        setNewProductData({
          categoryId: '',
          brand: '',
          model: '',
          name: '',
          purchaseRate: '',
          saleRate: '',
          openingStock: '10'
        });
        setShowNewProductModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create product');
      }
    }
  );

  const handleCreateProductSubmit = (e) => {
    e.preventDefault();
    if (!newProductData.categoryId || !newProductData.brand || !newProductData.model || !newProductData.name || !newProductData.purchaseRate || !newProductData.saleRate) {
      toast.error('All fields marked * are required');
      return;
    }
    createProductMutation.mutate({
      ...newProductData,
      branchId: selectedBranch?.id,
      purchaseRate: parseFloat(newProductData.purchaseRate) || 0,
      saleRate: parseFloat(newProductData.saleRate) || 0,
      openingStock: parseInt(newProductData.openingStock) || 0,
      minStockLimit: 5
    });
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      const target = e.target;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON' || target.type === 'submit' ||
        target.placeholder?.toLowerCase().includes('problem') || target.placeholder?.toLowerCase().includes('accessory')) {
        return;
      }
      e.preventDefault();
      const form = target.form;
      if (!form) return;
      const focusableElements = Array.from(form.elements).filter(el => {
        return (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') &&
          !el.disabled && el.type !== 'hidden' && el.tabIndex !== -1;
      });
      const currentIndex = focusableElements.indexOf(target);
      if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
        focusableElements[currentIndex + 1].focus();
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.customerPhone) {
      toast.error('Customer name and phone are required');
      return;
    }
    if (!formData.deviceBrand || !formData.deviceModel) {
      toast.error('Device brand and model are required');
      return;
    }
    const sparesTotal = spares.reduce((sum, item) => sum + (parseFloat(item.rate) * item.quantity), 0);
    const grandTotal = (parseFloat(formData.labourCost) || 0) + sparesTotal;

    const techniciansData = selectedTechnicians.length > 0
      ? selectedTechnicians
      : (formData.technicianName ? [{ id: '', name: formData.technicianName, phone: formData.technicianContact }] : []);

    createJobMutation.mutate({
      ...formData,
      estimatedCost: grandTotal,
      branchId: selectedBranch?.id,
      spares,
      problems,
      accessories,
      attachments,
      technicians: techniciansData
    });
  };

  const handleRemoveSpare = (indexToRemove) => {
    setSpares(spares.filter((_, idx) => idx !== indexToRemove));
  };

  const selectCustomer = (cust) => {
    setFormData(prev => ({
      ...prev,
      customerId: cust.id,
      customerName: cust.name,
      customerPhone: cust.phone,
      customerEmail: cust.email || '',
      customerAddress: cust.address || '',
      customerState: cust.state || '',
      customerGst: cust.gstNumber || ''
    }));
    setShowNameSuggestions(false);
    setShowPhoneSuggestions(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'customerName' || name === 'customerPhone' ? { customerId: '' } : {})
    }));
    if (name === 'customerName') {
      if (value.trim()) {
        api.get(`/api/customers?search=${value}&limit=10`)
          .then(res => { setNameSuggestions(res.data.data.customers || []); setShowNameSuggestions(true); })
          .catch(err => console.error(err));
      } else { setNameSuggestions([]); setShowNameSuggestions(false); }
    }
    if (name === 'customerPhone') {
      if (value.trim()) {
        api.get(`/api/customers?search=${value}&limit=10`)
          .then(res => { setPhoneSuggestions(res.data.data.customers || []); setShowPhoneSuggestions(true); })
          .catch(err => console.error(err));
      } else { setPhoneSuggestions([]); setShowPhoneSuggestions(false); }
    }
  };

  const openCamera = async () => {
    setShowCamera(true);
    setTimeout(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        console.error("Camera access error:", err);
        toast.error("Unable to access camera: " + err.message);
        setShowCamera(false);
      }
    }, 100);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
          setAttachments(prev => [...prev, file]);
          toast.success("Photo captured!");
          closeCamera();
        }
      }, 'image/jpeg', 0.95);
    }
  };

  const closeCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  const addProblem = () => {
    if (newProblem.trim()) {
      setProblems([...problems, { id: Date.now(), text: newProblem, checked: true }]);
      setNewProblem('');
    }
  };
  const removeProblem = (id) => setProblems(problems.filter(p => p.id !== id));

  const addAccessory = () => {
    if (newAccessory.trim()) {
      setAccessories([...accessories, { id: Date.now(), text: newAccessory, checked: true }]);
      setNewAccessory('');
    }
  };
  const removeAccessory = (id) => setAccessories(accessories.filter(a => a.id !== id));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Create New Job</h2>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              {/* Job Detail */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Job Detail</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Job ID</label>
                    <input type="text" value="Auto Generated" disabled className="input-field bg-gray-100" />
                  </div>
                  <div>
                    <label className="form-label">Date</label>
                    <input type="date" name="jobDate" value={formData.jobDate} onChange={handleChange} className="input-field" />
                  </div>
                  <div>
                    <label className="form-label">Branch</label>
                    <input type="text" value={selectedBranch?.name || ''} disabled className="input-field bg-gray-100" />
                  </div>
                </div>
              </div>

              {/* Customer Detail */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Customer Detail</h3>
                <div className="space-y-3">
                  <div className="relative">
                    <label className="form-label">Name *</label>
                    <input type="text" name="customerName" value={formData.customerName} onChange={handleChange}
                      onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)} className="input-field"
                      placeholder="Customer Name" autoComplete="off" required />
                    {showNameSuggestions && nameSuggestions.length > 0 && (
                      <div className="absolute z-[100] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {nameSuggestions.map(cust => (
                          <div key={cust.id} onMouseDown={() => selectCustomer(cust)}
                            className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm text-gray-700 flex justify-between items-center">
                            <span className="font-semibold">{cust.name}</span>
                            <span className="text-gray-400 text-xs">{cust.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <label className="form-label">Contact *</label>
                    <input type="tel" name="customerPhone" value={formData.customerPhone} onChange={handleChange}
                      onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 200)} className="input-field"
                      placeholder="+91-XXXXXXXXXX" autoComplete="off" required />
                    {showPhoneSuggestions && phoneSuggestions.length > 0 && (
                      <div className="absolute z-[100] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {phoneSuggestions.map(cust => (
                          <div key={cust.id} onMouseDown={() => selectCustomer(cust)}
                            className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm text-gray-700 flex justify-between items-center">
                            <span className="font-semibold">{cust.phone}</span>
                            <span className="text-gray-400 text-xs">{cust.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Email</label>
                    <input type="email" name="customerEmail" value={formData.customerEmail} onChange={handleChange} className="input-field" placeholder="customer@example.com" />
                  </div>
                  <div>
                    <label className="form-label">Address</label>
                    <textarea name="customerAddress" value={formData.customerAddress} onChange={handleChange} className="input-field" rows="2" placeholder="Full Address" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label">State</label>
                      <input type="text" name="customerState" value={formData.customerState} onChange={handleChange} className="input-field" placeholder="State" />
                    </div>
                    <div>
                      <label className="form-label">GST Number</label>
                      <input type="text" name="customerGst" value={formData.customerGst} onChange={handleChange} className="input-field" placeholder="GST Number" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Device Detail */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Device Detail</h3>
                <div className="space-y-3">
                  <div>
                    <label className="form-label">Category *</label>
                    <select name="deviceCategory" value={formData.deviceCategory} onChange={handleChange} className="input-field" required>
                      <option value="">-- Select Category --</option>
                      {(categoriesData || []).map((cat) => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label">Brand *</label>
                      <input type="text" name="deviceBrand" value={formData.deviceBrand} onChange={handleChange} className="input-field" placeholder="e.g., Apple, Samsung" required />
                    </div>
                    <div>
                      <label className="form-label">Model *</label>
                      <input type="text" name="deviceModel" value={formData.deviceModel} onChange={handleChange} className="input-field" placeholder="e.g., iPhone 13" required />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">IMEI / Serial No</label>
                    <input type="text" name="deviceImei" value={formData.deviceImei} onChange={handleChange} className="input-field" placeholder="IMEI or Serial Number" />
                  </div>
                  <div>
                    <label className="form-label">Password / Pattern</label>
                    <input type="text" name="devicePassword" value={formData.devicePassword} onChange={handleChange} className="input-field" placeholder="Device Password" />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Technician Detail */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Technician Detail</h3>
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <div className="flex-1">
                      <label className="form-label">Assign Technician</label>
                      <Select
                        options={(techniciansData || []).map(tech => ({
                          value: tech.id,
                          label: `${tech.name}${tech.phone ? ` - ${tech.phone}` : ''}${tech.specialization ? ` (${tech.specialization})` : ''}`,
                          phone: tech.phone || '',
                          specialization: tech.specialization || ''
                        }))}
                        value={(() => {
                          if (!currentTechnicianId) return null;
                          const tech = (techniciansData || []).find(t => t.id === currentTechnicianId);
                          if (!tech) return null;
                          return {
                            value: tech.id,
                            label: `${tech.name}${tech.phone ? ` - ${tech.phone}` : ''}${tech.specialization ? ` (${tech.specialization})` : ''}`,
                            phone: tech.phone || '',
                            specialization: tech.specialization || ''
                          };
                        })()}
                        onChange={(val) => setCurrentTechnicianId(val ? val.value : '')}
                        placeholder="Type to search technician..."
                        isSearchable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        noOptionsMessage={() => 'No technician found'}
                      />
                    </div>
                    <div className="flex items-end">
                      <button type="button" onClick={() => {
                        if (!currentTechnicianId) { toast.error('Please select a technician first'); return; }
                        const tech = (techniciansData || []).find(t => t.id === currentTechnicianId);
                        if (!tech) return;
                        if (selectedTechnicians.find(t => t.id === tech.id)) { toast.error('Technician already added'); return; }
                        setSelectedTechnicians([...selectedTechnicians, { id: tech.id, name: tech.name, phone: tech.phone || '', specialization: tech.specialization || '' }]);
                        setCurrentTechnicianId('');
                        toast.success(`${tech.name} added`);
                      }} className="bg-green-600 hover:bg-green-700 text-white font-bold p-2.5 rounded-lg transition-colors" title="Add Technician">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  {selectedTechnicians.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-700 block">Added Technicians:</label>
                      {selectedTechnicians.map((tech, idx) => (
                        <div key={tech.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                          <div className="flex items-center space-x-2">
                            <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                            <div>
                              <span className="text-sm font-medium text-gray-800">{tech.name}</span>
                              {tech.phone && <span className="text-xs text-gray-500 ml-2">{tech.phone}</span>}
                              {tech.specialization && <span className="text-xs text-gray-400 ml-1">({tech.specialization})</span>}
                            </div>
                          </div>
                          <button type="button" onClick={() => setSelectedTechnicians(selectedTechnicians.filter((_, i) => i !== idx))}
                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Cost Detail */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2 flex justify-between items-center">
                  <span>Cost Detail</span>
                  <button type="button" onClick={() => { setTempSpares([]); setPopupSelectedSpare(null); setPopupSparePrice(''); setPopupSpareQty(1); setShowAddSparesModal(true); }}
                    className="btn-primary py-1 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">+ Add Spare Parts</button>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">Box No</label>
                    <input type="text" name="boxNumber" value={formData.boxNumber} onChange={handleChange} className="input-field" placeholder="Storage Box" />
                  </div>
                  <div>
                    <label className="form-label">Labour</label>
                    <input type="number" name="labourCost" value={formData.labourCost} onChange={handleChange} className="input-field" placeholder="0.00" step="0.01" />
                  </div>
                  <div>
                    <label className="form-label">Advance</label>
                    <input type="number" name="advanceAmount" value={formData.advanceAmount} onChange={handleChange} className="input-field" placeholder="0.00" step="0.01" />
                  </div>
                  {spares.length > 0 && (
                    <div className="col-span-1 sm:col-span-2 mt-3 space-y-2 border-t pt-3">
                      <label className="text-xs font-semibold text-gray-700 block">Added Spares:</label>
                      <div className="space-y-1">
                        {spares.map((spare, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-white p-2 rounded border border-gray-200">
                            <span className="text-sm font-medium text-gray-800">{spare.name}</span>
                            <button type="button" onClick={() => handleRemoveSpare(idx)} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="col-span-1 sm:col-span-2 mt-4 pt-3 border-t border-dashed flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Spares Cost total:</span>
                    <span className="text-sm font-semibold text-gray-900">{currencySymbol}{spares.reduce((sum, item) => sum + (parseFloat(item.rate) * item.quantity), 0).toFixed(2)}</span>
                  </div>
                  <div className="col-span-1 sm:col-span-2 flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Grand Total:</span>
                    <span className="text-lg font-bold text-green-600">
                      {currencySymbol}{((parseFloat(formData.labourCost) || 0) + spares.reduce((sum, item) => sum + (parseFloat(item.rate) * item.quantity), 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Problems */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Problems</h3>
                <div className="space-y-2">
                  {problems.map(problem => (
                    <div key={problem.id} className="flex items-center justify-between bg-white p-2 rounded">
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" checked={problem.checked} readOnly className="rounded" />
                        <span className="text-sm">{problem.text}</span>
                      </div>
                      <button type="button" onClick={() => removeProblem(problem.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex space-x-2">
                    <input type="text" value={newProblem} onChange={(e) => setNewProblem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addProblem())} className="input-field flex-1" placeholder="Add problem..." />
                    <button type="button" onClick={addProblem} className="btn-primary"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>

              {/* Accessories */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Accessories</h3>
                <div className="space-y-2">
                  {accessories.map(accessory => (
                    <div key={accessory.id} className="flex items-center justify-between bg-white p-2 rounded">
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" checked={accessory.checked} readOnly className="rounded" />
                        <span className="text-sm">{accessory.text}</span>
                      </div>
                      <button type="button" onClick={() => removeAccessory(accessory.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex space-x-2">
                    <input type="text" value={newAccessory} onChange={(e) => setNewAccessory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAccessory())} className="input-field flex-1" placeholder="Add accessory..." />
                    <button type="button" onClick={addAccessory} className="btn-primary"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Additional Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="form-label">Problem Description *</label>
                    <textarea name="problemDescription" value={formData.problemDescription} onChange={handleChange} className="input-field" rows="3" placeholder="Describe the problem..." required />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="form-label">Priority</label>
                      <select name="priority" value={formData.priority} onChange={handleChange} className="input-field">
                        <option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Expected Date</label>
                      <input type="date" name="expectedDate" value={formData.expectedDate} onChange={handleChange} className="input-field" />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Attachments</label>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <input type="file" multiple onChange={handleFileChange} className="hidden" id="file-upload" accept="image/*,video/*,.pdf" />
                      <label htmlFor="file-upload" className="btn-secondary cursor-pointer inline-flex items-center">
                        <Upload className="w-4 h-4 mr-2" /> Upload Files
                      </label>
                      <button type="button" onClick={openCamera} className="btn-secondary inline-flex items-center bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200">
                        <Camera className="w-4 h-4 mr-2" /> Take Photo
                      </button>
                      <span className="text-sm text-gray-500">{attachments.length} file(s) selected</span>
                    </div>
                    {attachments.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto p-1 bg-white border border-gray-100 rounded-lg">
                        {attachments.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                            <span className="truncate flex-1 pr-2 font-medium">{file.name}</span>
                            <button type="button" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end space-x-4 border-t pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={createJobMutation.isLoading} className="btn-primary">
              {createJobMutation.isLoading ? <><div className="spinner mr-2"></div>Creating...</> : 'Create Job'}
            </button>
          </div>
        </form>

        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
              <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
                <h3 className="font-bold">Live Camera Capture</h3>
                <button type="button" onClick={closeCamera} className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="bg-black relative aspect-video flex items-center justify-center overflow-hidden">
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <div className="p-4 flex justify-between bg-gray-50 border-t">
                <button type="button" onClick={closeCamera} className="btn-secondary">Cancel</button>
                <button type="button" onClick={capturePhoto} className="btn-primary flex items-center"><Camera className="w-4 h-4 mr-2" /> Capture</button>
              </div>
            </div>
          </div>
        )}

        {showAddSparesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
                <h2 className="text-xl font-bold">Add Spare Parts</h2>
                <button type="button" onClick={() => setShowAddSparesModal(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="form-label block text-sm font-medium text-gray-700">Search Spare (Type Name)</label>
                    <button type="button" onClick={() => setShowNewProductModal(true)} className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold">+ Add New Product</button>
                  </div>
                  <Select options={productsData?.map(p => ({ value: p.id, label: `${p.name} (${p.brand} ${p.model}) - Stock: ${p.currentStock}`, price: p.saleRate, stock: p.currentStock, name: p.name }))}
                    value={popupSelectedSpare} onChange={(val) => { setPopupSelectedSpare(val); setPopupSparePrice(val ? val.price.toString() : ''); }}
                    placeholder="Type to search spares..." isSearchable className="react-select-container" classNamePrefix="react-select" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label block text-sm font-medium text-gray-700 mb-1">Price ({currencySymbol})</label>
                    <input type="number" step="0.01" className="input-field" value={popupSparePrice} onChange={(e) => setPopupSparePrice(e.target.value)} placeholder="Price" disabled={!popupSelectedSpare} />
                  </div>
                  <div>
                    <label className="form-label block text-sm font-medium text-gray-700 mb-1">Qty</label>
                    <input type="number" min="1" className="input-field" value={popupSpareQty} onChange={(e) => setPopupSpareQty(parseInt(e.target.value) || 1)} disabled={!popupSelectedSpare} />
                  </div>
                </div>
                <div>
                  <label className="form-label block text-sm font-medium text-gray-700 mb-1">Total ({currencySymbol})</label>
                  <input type="text" className="input-field bg-gray-50 text-gray-700 font-semibold" value={((parseFloat(popupSparePrice) || 0) * popupSpareQty).toFixed(2)} disabled />
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => {
                    if (!popupSelectedSpare) { toast.error('Please select a spare part first'); return; }
                    const rate = parseFloat(popupSparePrice);
                    if (isNaN(rate) || rate < 0) { toast.error('Please enter a valid price'); return; }
                    setTempSpares([...tempSpares, { productId: popupSelectedSpare.value, name: popupSelectedSpare.name, rate, quantity: popupSpareQty }]);
                    setPopupSelectedSpare(null); setPopupSparePrice(''); setPopupSpareQty(1);
                    toast.success('Spare part added to list');
                  }} disabled={!popupSelectedSpare} className="btn-primary px-4 py-2">Add Spare</button>
                </div>
                {tempSpares.length > 0 && (
                  <div className="border-t pt-4">
                    <label className="text-xs font-semibold text-gray-700 block mb-2">Spares Added in Current Session:</label>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {tempSpares.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded border text-xs">
                          <span>{item.name} ({item.quantity} x {currencySymbol}{item.rate.toFixed(2)})</span>
                          <button type="button" onClick={() => setTempSpares(tempSpares.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">Remove</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                  <button type="button" onClick={() => setShowAddSparesModal(false)} className="btn-secondary">Cancel</button>
                  <button type="button" onClick={() => {
                    if (tempSpares.length === 0) { toast.error('Please add at least one spare part or cancel'); return; }
                    setSpares([...spares, ...tempSpares]); setTempSpares([]); setShowAddSparesModal(false);
                    toast.success('Spares saved successfully!');
                  }} className="btn-primary">Submit</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showNewProductModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[120] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">Add New Spare Product</h3>
                <button type="button" onClick={() => setShowNewProductModal(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreateProductSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Category *</label>
                    <select className="input-field text-sm" value={newProductData.categoryId} onChange={(e) => setNewProductData({ ...newProductData, categoryId: e.target.value })} required>
                      <option value="">Select Category</option>
                      {categoriesData?.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Brand *</label>
                    <input type="text" className="input-field text-sm" value={newProductData.brand} onChange={(e) => setNewProductData({ ...newProductData, brand: e.target.value })} placeholder="e.g. Apple, Samsung" required />
                  </div>
                  <div>
                    <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Model *</label>
                    <input type="text" className="input-field text-sm" value={newProductData.model} onChange={(e) => setNewProductData({ ...newProductData, model: e.target.value })} placeholder="e.g. iPhone 13 Pro" required />
                  </div>
                  <div>
                    <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Item Name *</label>
                    <input type="text" className="input-field text-sm" value={newProductData.name} onChange={(e) => setNewProductData({ ...newProductData, name: e.target.value })} placeholder="e.g. Battery, Screen" required />
                  </div>
                  <div>
                    <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Purchase Rate *</label>
                    <input type="number" step="0.01" className="input-field text-sm" value={newProductData.purchaseRate} onChange={(e) => setNewProductData({ ...newProductData, purchaseRate: e.target.value })} placeholder="0.00" required />
                  </div>
                  <div>
                    <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Sale Rate *</label>
                    <input type="number" step="0.01" className="input-field text-sm" value={newProductData.saleRate} onChange={(e) => setNewProductData({ ...newProductData, saleRate: e.target.value })} placeholder="0.00" required />
                  </div>
                </div>
                <div>
                  <label className="form-label block text-xs font-semibold text-gray-700 mb-1">Initial Stock *</label>
                  <input type="number" className="input-field text-sm" value={newProductData.openingStock} onChange={(e) => setNewProductData({ ...newProductData, openingStock: e.target.value })} required />
                </div>
                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowNewProductModal(false)} className="btn-secondary text-xs">Cancel</button>
                  <button type="submit" disabled={createProductMutation.isLoading} className="btn-primary text-xs flex items-center">
                    {createProductMutation.isLoading && <div className="spinner mr-1.5"></div>}
                    Create Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateJobModal;